import React, { useEffect, useRef, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from './AuthWrapper';
import { Cpu, ShieldCheck, Zap, Database, Menu, X, LogOut, ChevronDown, Terminal, AudioWaveform, ArrowRight, Code2, GitBranch, Bug, FileCode, Sparkles, Layers, Clock, TrendingUp, ChevronLeft, ChevronRight, Rocket, Lock, FileCheck, Activity, LayoutDashboard } from 'lucide-react';
import Logger from '../utils/logger';

// Try to import logo, fallback if not available
const logo = '/elinnos-logo.png';

const LandingPageExact = () => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const observerRef = useRef(null);
  const [userMenuOpen, setUserMenuOpen] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const userMenuRef = useRef(null);
  const [showcaseIndex, setShowcaseIndex] = useState(0);
  const showcaseTimerRef = useRef(null);
  const [heroSlideIndex, setHeroSlideIndex] = useState(0);
  const heroSlideTimerRef = useRef(null);

  // Contact form state
  const [contactForm, setContactForm] = useState({ name: '', email: '', company: '', seats: '', message: '' });
  const [contactSubmitting, setContactSubmitting] = useState(false);
  const [contactSuccess, setContactSuccess] = useState(false);
  const [contactError, setContactError] = useState('');

  useEffect(() => {
    // Initialize IntersectionObserver for fade-in animations
    const observerOptions = {
      threshold: 0.1,
      rootMargin: '0px 0px -50px 0px'
    };

    observerRef.current = new IntersectionObserver((entries) => {
      entries.forEach(entry => {
        if (entry.isIntersecting) {
          entry.target.classList.add('fade-in');
        } else if (entry.target.classList.contains('scroll-reveal')) {
          entry.target.classList.remove('fade-in');
        }
      });
    }, observerOptions);

    // Observe all card-hover elements
    document.querySelectorAll('.card-hover').forEach(el => {
      observerRef.current.observe(el);
    });

    // Observe all scroll-reveal sections (slight delay to ensure DOM is ready)
    setTimeout(() => {
      document.querySelectorAll('section.scroll-reveal').forEach(el => {
        observerRef.current.observe(el);
      });
    }, 100);

    // Close user menu when clicking outside
    const handleClickOutside = (event) => {
      if (userMenuRef.current && !userMenuRef.current.contains(event.target)) {
        setUserMenuOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);

    return () => {
      if (observerRef.current) {
        observerRef.current.disconnect();
      }

      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  useEffect(() => {
    showcaseTimerRef.current = setInterval(() => {
      setShowcaseIndex(prev => (prev + 1) % 2);
    }, 5000);
    return () => clearInterval(showcaseTimerRef.current);
  }, []);

  useEffect(() => {
    heroSlideTimerRef.current = setInterval(() => {
      setHeroSlideIndex(prev => (prev + 1) % 3);
    }, 4000);
    return () => clearInterval(heroSlideTimerRef.current);
  }, []);

  // Handle ?section= query param (navigating from dashboard)
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const section = params.get('section');
    if (section) {
      setTimeout(() => {
        const element = document.getElementById(section);
        if (element) {
          element.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }
      }, 300);
      // Clean the URL
      window.history.replaceState({}, document.title, '/');
    } else {
      window.scrollTo(0, 0);
    }
  }, [location.search]);

  const handleSignIn = (e) => {
    if (e) e.preventDefault();
    const params = new URLSearchParams(window.location.search);
    const existingRedirect = params.get('redirect');
    const redirect = existingRedirect || 'landing';
    Logger.debug('Sign In clicked, navigating to /dashboard', { redirect });
    navigate(`/dashboard?redirect=${encodeURIComponent(redirect)}`);
  };

  const handleLogout = async (e) => {
    if (e) e.preventDefault();
    Logger.debug('Logout clicked');
    await logout();
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };
  
  const scrollToSection = (sectionId) => {
    const element = document.getElementById(sectionId);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  };

  return (
    <div className="hero-ref-bg text-slate-100 min-h-screen relative page-transition">

      {/* Navigation */}
      <nav className="fixed top-0 w-full header-ref-nav z-50 transition-colors">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-14 sm:h-16">
            {/* Left Brand: elinnos | chipdesigner.ai */}
            <div className="flex items-center space-x-3">
              {logo ? (
                <a href="/" className="hover:opacity-80 transition-opacity flex items-center gap-2">
                  <img src={logo} alt="Elinnos" className="h-12 w-auto" />
                </a>
              ) : (
                <a href="/" className="text-xl font-extrabold tracking-tight text-white hover:text-cyan-400 transition-colors">
                  elinnos
                </a>
              )}
              <span className="text-slate-600 font-light text-lg">|</span>
              <a href="/" className="text-base sm:text-lg font-bold text-transparent bg-clip-text bg-gradient-to-r from-cyan-400 to-blue-400 hover:opacity-80 transition-opacity">
                chipdesigner.ai
              </a>
            </div>
            
            {/* Center Navigation Links */}
            <div className="hidden md:flex items-center space-x-8">
              <button 
                type="button"
                onClick={() => scrollToSection('features')} 
                className="nav-tab"
              >
                Features
              </button>
              <button 
                type="button"
                onClick={() => scrollToSection('demo')} 
                className="nav-tab"
              >
                Platform
              </button>
              <button 
                type="button"
                onClick={() => navigate('/dashboard')} 
                className="nav-tab"
              >
                Dashboard
              </button>
              <button 
                type="button"
                onClick={() => navigate('/pricing')} 
                className="nav-tab"
              >
                Pricing
              </button>
              <button 
                type="button"
                onClick={() => scrollToSection('contact')} 
                className="nav-tab"
              >
                Contact
              </button>
              <button 
                type="button"
                onClick={() => navigate('/downloads')} 
                className="nav-tab"
              >
                Download
              </button>
            </div>

            {/* Right User Actions */}
            <div className="hidden md:flex items-center space-x-4">
              {user ? (
                <div className="relative" ref={userMenuRef}>
                  <button
                    type="button"
                    onClick={() => setUserMenuOpen(!userMenuOpen)}
                    className="flex items-center gap-2.5 px-3 py-1.5 rounded-full bg-slate-800/80 hover:bg-slate-700/80 border border-slate-700/60 transition-all cursor-pointer"
                  >
                    <div className="w-7 h-7 rounded-full bg-gradient-to-tr from-blue-600 to-cyan-400 flex items-center justify-center text-white font-bold text-xs shadow-sm">
                      {user?.name?.charAt(0).toUpperCase() || 'P'}
                    </div>
                    <ChevronDown className={`w-3.5 h-3.5 text-slate-400 transition-transform ${userMenuOpen ? 'rotate-180' : ''}`} />
                  </button>
                  
                  {/* Dropdown Menu */}
                  {userMenuOpen && (
                    <div className="absolute right-0 mt-2 w-48 bg-slate-900/95 backdrop-blur-xl rounded-xl shadow-2xl border border-slate-700/60 py-2 z-50">
                      <div className="px-4 py-2 border-b border-slate-800">
                        <p className="text-sm font-semibold text-white">{user?.name || 'User'}</p>
                        <p className="text-xs text-slate-400 truncate">{user?.email || ''}</p>
                      </div>
                      <button
                        type="button"
                        onClick={handleLogout}
                        className="w-full flex items-center gap-2 px-4 py-2 text-sm text-red-400 hover:bg-red-500/10 transition-colors cursor-pointer text-left"
                        style={{ background: 'none', border: 'none', font: 'inherit' }}
                      >
                        <LogOut className="w-4 h-4" />
                        Logout
                      </button>
                    </div>
                  )}
                </div>
              ) : (
                <button 
                  type="button"
                  onClick={handleSignIn}
                  className="bg-gradient-to-r from-blue-600 to-cyan-500 hover:from-blue-500 hover:to-cyan-400 text-white px-5 py-2 rounded-lg text-sm font-medium shadow-[0_0_15px_rgba(6,182,212,0.3)] transition-all cursor-pointer"
                  style={{ border: 'none' }}
                >
                  Sign In
                </button>
              )}
            </div>
            
            <div className="md:hidden">
              <button 
                type="button"
                onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
                className="text-slate-300 hover:text-white"
              >
                {mobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
              </button>
            </div>
          </div>
        </div>
        
        {/* Mobile Menu Dropdown */}
        {mobileMenuOpen && (
          <div className="md:hidden border-t border-slate-800/60 bg-[#0a0e1a]/95 backdrop-blur-xl">
            <div className="px-4 py-4 space-y-1">
              <button 
                type="button"
                onClick={() => { scrollToSection('features'); setMobileMenuOpen(false); }} 
                className="block w-full text-left py-2.5 text-slate-300 hover:text-white transition-colors"
              >
                Features
              </button>
              <button 
                type="button"
                onClick={() => { scrollToSection('demo'); setMobileMenuOpen(false); }} 
                className="block w-full text-left py-2.5 text-slate-300 hover:text-white transition-colors"
              >
                Platform
              </button>
              <button 
                type="button"
                onClick={() => { navigate('/dashboard'); setMobileMenuOpen(false); }} 
                className="block w-full text-left py-2.5 text-slate-300 hover:text-white transition-colors"
              >
                Dashboard
              </button>
              <button 
                type="button"
                onClick={() => { scrollToSection('contact'); setMobileMenuOpen(false); }} 
                className="block w-full text-left py-2.5 text-slate-300 hover:text-white transition-colors"
              >
                Contact
              </button>
              <button 
                type="button"
                onClick={() => { navigate('/downloads'); setMobileMenuOpen(false); }} 
                className="block w-full text-left py-2.5 text-slate-300 hover:text-white transition-colors"
              >
                Download
              </button>
              <div className="pt-3 border-t border-slate-800/60">
                {user ? (
                  <button 
                    type="button"
                    onClick={() => { handleLogout(); setMobileMenuOpen(false); }}
                    className="flex items-center gap-2 py-2.5 text-red-400 hover:text-red-300 transition-colors"
                  >
                    <LogOut className="w-4 h-4" /> Logout
                  </button>
                ) : (
                  <button 
                    type="button"
                    onClick={() => { handleSignIn(); setMobileMenuOpen(false); }}
                    className="block w-full text-center py-2.5 bg-gradient-to-r from-blue-600 to-cyan-500 hover:from-blue-500 hover:to-cyan-400 text-white rounded-lg text-sm font-medium transition-all"
                  >
                    Sign In
                  </button>
                )}
              </div>
            </div>
          </div>
        )}
      </nav>

      {/* Hero Section — Reference Design Layout with 3D Chip + IDE Composition */}
      <section className="hero-ref-bg circuit-trace-glow relative pt-28 sm:pt-32 pb-20 overflow-hidden">
        {/* Ambient Top Glow */}
        <div className="absolute top-10 left-1/4 w-[500px] h-[300px] bg-blue-600/10 blur-[100px] rounded-full pointer-events-none" />
        <div className="absolute top-20 right-10 w-[600px] h-[400px] bg-cyan-500/10 blur-[120px] rounded-full pointer-events-none" />

        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-center">
            
            {/* Left Column — Marketing Copy & CTAs (5 cols) */}
            <div className="lg:col-span-5 text-center lg:text-left">
              <div className="dl-fade-up inline-flex items-center gap-2 px-3.5 py-1.5 rounded-full mb-6 bg-blue-950/60 backdrop-blur-md border border-cyan-500/30 shadow-[0_0_15px_rgba(6,182,212,0.2)]">
                <Sparkles className="w-3.5 h-3.5 text-cyan-400" />
                <span className="text-xs font-semibold uppercase tracking-wider text-cyan-300">
                  AI-Powered VLSI Design Platform
                </span>
              </div>
              
              <h1 key={heroSlideIndex} className="dl-fade-up-delay-1 text-3xl sm:text-4xl lg:text-5xl font-extrabold tracking-tight text-white mb-6 leading-[1.12] hero-text-innovative">
                {heroSlideIndex === 0 ? (
                  <>
                    <span className="hero-text-word" style={{ animationDelay: '0ms' }}>Chipdesigner</span><span className="text-transparent bg-clip-text bg-gradient-to-r from-blue-400 via-cyan-300 to-indigo-400 hero-text-word" style={{ animationDelay: '100ms' }}>.ai</span><br />
                    <span className="hero-text-word" style={{ animationDelay: '200ms' }}>IDE</span>
                  </>
                ) : heroSlideIndex === 1 ? (
                  <>
                    <span className="hero-text-word" style={{ animationDelay: '0ms' }}>Integrated&nbsp;</span><span className="text-transparent bg-clip-text bg-gradient-to-r from-cyan-400 to-blue-400 hero-text-word" style={{ animationDelay: '100ms' }}>Pulse</span><br />
                    <span className="hero-text-word" style={{ animationDelay: '200ms' }}>SV/UVM Simulator</span><br />
                    <span className="hero-text-word" style={{ animationDelay: '300ms' }}>Waveform Viewer</span>
                  </>
                ) : (
                  <>
                    <span className="hero-text-word" style={{ animationDelay: '0ms' }}>Integrated&nbsp;</span><span className="text-transparent bg-clip-text bg-gradient-to-r from-cyan-400 to-blue-400 hero-text-word" style={{ animationDelay: '100ms' }}>Pulse</span><br />
                    <span className="hero-text-word" style={{ animationDelay: '200ms' }}>SV/UVM Coverage</span><br />
                    <span className="hero-text-word" style={{ animationDelay: '300ms' }}>Analysis</span>
                  </>
                )}
              </h1>

              <p key={`desc-${heroSlideIndex}`} className="dl-fade-up-delay-2 text-base sm:text-lg text-slate-300/80 max-w-xl mx-auto lg:mx-0 leading-relaxed mb-8 hero-text-innovative">
                {heroSlideIndex === 0
                  ? 'End-to-end AI copilot for RTL design, verification, and EDA integration — built for modern silicon teams.'
                  : heroSlideIndex === 1
                    ? 'Visualize simulation results with signal traces, cursors, and value inspection — seamlessly integrated into Chipdesigner.ai IDE.'
                    : 'Track functional coverage metrics, identify uncovered code paths, and close verification gaps — all within the Chipdesigner.ai IDE.'}
              </p>
              
              <div className="dl-fade-up-delay-3 flex flex-wrap gap-4 justify-center lg:justify-start">
                <button 
                  type="button"
                  onClick={() => scrollToSection('features')}
                  className="bg-gradient-to-r from-blue-600 via-indigo-600 to-purple-600 hover:from-blue-500 hover:via-indigo-500 hover:to-purple-500 text-white px-7 py-3.5 text-base rounded-xl font-medium inline-flex items-center gap-2 shadow-[0_0_25px_rgba(59,130,246,0.5)] transition-all cursor-pointer"
                  style={{ border: 'none' }}
                >
                  Explore Features <ArrowRight className="w-4 h-4" />
                </button>
                {user ? (
                  <button
                    type="button"
                    onClick={() => navigate('/dashboard')}
                    className="border border-cyan-500/30 bg-slate-900/80 hover:bg-slate-800/80 text-cyan-200 px-6 py-3.5 text-base rounded-xl font-medium inline-flex items-center gap-2 backdrop-blur-md transition-all cursor-pointer shadow-[0_0_15px_rgba(6,182,212,0.15)]"
                  >
                    <LayoutDashboard className="w-4 h-4 text-cyan-400" /> Go to Dashboard
                  </button>
                ) : (
                  <a
                    href="https://elinnos.com"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="border border-cyan-500/30 bg-slate-900/80 hover:bg-slate-800/80 text-cyan-200 px-6 py-3.5 text-base rounded-xl font-medium inline-flex items-center gap-2 backdrop-blur-md transition-all cursor-pointer shadow-[0_0_15px_rgba(6,182,212,0.15)]"
                  >
                    <LayoutDashboard className="w-4 h-4 text-cyan-400" /> Back to Elinnos
                  </a>
                )}
              </div>
            </div>

            {/* Right Column — 3D Chip + IDE Visual Composition (7 cols) */}
            <div className="lg:col-span-7 dl-fade-up-delay-2 relative">
              <div className="hero-composition-wrap">
                
                {/* IDE Screenshot — clean, no terminal border, bigger, sliding carousel */}
                <div className="hero-ide-clean">
                  <div className="hero-ide-viewport">
                    <div className="hero-ide-track" style={{ transform: `translateX(-${heroSlideIndex * 100}%)` }}>
                      <div className="hero-ide-slide">
                        <img 
                          src="/cder-ide.png" 
                          alt="ChipDesigner.ai IDE Interface" 
                          className="hero-ide-clean-img"
                        />
                      </div>
                      <div className="hero-ide-slide">
                        <img 
                          src="/cder-elwave.png" 
                          alt="ChipDesigner.ai ElWave Viewer" 
                          className="hero-ide-clean-img"
                        />
                      </div>
                      <div className="hero-ide-slide">
                        <img 
                          src="/cov123.png" 
                          alt="ChipDesigner.ai Pulse Coverage Analysis" 
                          className="hero-ide-clean-img"
                        />
                      </div>
                    </div>
                  </div>
                  <div className="hero-ide-dots">
                    <button
                      type="button"
                      className={`hero-ide-dot ${heroSlideIndex === 0 ? 'hero-ide-dot-active' : ''}`}
                      onClick={() => setHeroSlideIndex(0)}
                      aria-label="Show IDE"
                    />
                    <button
                      type="button"
                      className={`hero-ide-dot ${heroSlideIndex === 1 ? 'hero-ide-dot-active' : ''}`}
                      onClick={() => setHeroSlideIndex(1)}
                      aria-label="Show ElWave"
                    />
                    <button
                      type="button"
                      className={`hero-ide-dot ${heroSlideIndex === 2 ? 'hero-ide-dot-active' : ''}`}
                      onClick={() => setHeroSlideIndex(2)}
                      aria-label="Show Coverage"
                    />
                  </div>
                </div>

                {/* 3D Elinnos Chip — background, blended with fade */}
                <div className="hero-chip-foreground">
                  <div className="hero-chip-gradient-blend" />
                  <img 
                    src="/Chip3d.png" 
                    alt="Elinnos 3D AI Silicon Chip" 
                    className="hero-chip-img-blend"
                  />
                </div>

              </div>
            </div>

          </div>

          {/* 4 Feature Cards (Directly below Hero visual, matching reference layout!) */}
          <div id="features" className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5 mt-16 sm:mt-20">
            {/* Card 1 */}
            <div className="feature-ref-card group">
              <div className="feature-icon-box bg-blue-500/10 border-blue-400/20 text-blue-400 group-hover:shadow-[0_0_20px_rgba(59,130,246,0.4)] transition-all">
                <Cpu className="w-6 h-6" />
              </div>
              <h3 className="text-base font-semibold text-white mb-2 group-hover:text-cyan-300 transition-colors">
                AI-Powered RTL Generation
              </h3>
              <p className="text-xs sm:text-sm text-slate-400 leading-relaxed mb-4">
                Generate clean, synthesizable RTL code in seconds.
              </p>
              <div className="flex justify-end">
                <ArrowRight className="w-4 h-4 text-slate-500 group-hover:text-cyan-400 group-hover:translate-x-1 transition-all" />
              </div>
            </div>
            
            {/* Card 2 */}
            <div className="feature-ref-card group">
              <div className="feature-icon-box bg-purple-500/10 border-purple-400/20 text-purple-400 group-hover:shadow-[0_0_20px_rgba(168,85,247,0.4)] transition-all">
                <Activity className="w-6 h-6" />
              </div>
              <h3 className="text-base font-semibold text-white mb-2 group-hover:text-purple-300 transition-colors">
                Integrated Simulation
              </h3>
              <p className="text-xs sm:text-sm text-slate-400 leading-relaxed mb-4">
                Simulate, debug, and analyze designs seamlessly.
              </p>
              <div className="flex justify-end">
                <ArrowRight className="w-4 h-4 text-slate-500 group-hover:text-purple-400 group-hover:translate-x-1 transition-all" />
              </div>
            </div>
            
            {/* Card 3 */}
            <div className="feature-ref-card group">
              <div className="feature-icon-box bg-cyan-500/10 border-cyan-400/20 text-cyan-400 group-hover:shadow-[0_0_20px_rgba(6,182,212,0.4)] transition-all">
                <ShieldCheck className="w-6 h-6" />
              </div>
              <h3 className="text-base font-semibold text-white mb-2 group-hover:text-cyan-300 transition-colors">
                Secure & Reliable
              </h3>
              <p className="text-xs sm:text-sm text-slate-400 leading-relaxed mb-4">
                Enterprise-grade security and IP protection.
              </p>
              <div className="flex justify-end">
                <ArrowRight className="w-4 h-4 text-slate-500 group-hover:text-cyan-400 group-hover:translate-x-1 transition-all" />
              </div>
            </div>

            {/* Card 4 */}
            <div className="feature-ref-card group">
              <div className="feature-icon-box bg-indigo-500/10 border-indigo-400/20 text-indigo-400 group-hover:shadow-[0_0_20px_rgba(99,102,241,0.4)] transition-all">
                <Zap className="w-6 h-6" />
              </div>
              <h3 className="text-base font-semibold text-white mb-2 group-hover:text-indigo-300 transition-colors">
                EDA Integration
              </h3>
              <p className="text-xs sm:text-sm text-slate-400 leading-relaxed mb-4">
                Plug into your favorite EDA tools and workflows.
              </p>
              <div className="flex justify-end">
                <ArrowRight className="w-4 h-4 text-slate-500 group-hover:text-indigo-400 group-hover:translate-x-1 transition-all" />
              </div>
            </div>
          </div>

          {/* Stats Band (Directly below Feature Cards, matching reference mockup!) */}
          <div className="stats-ref-container mt-8 sm:mt-10">
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-6 sm:gap-8 items-center">
              {/* Stat 1 */}
              <div className="flex items-center gap-3.5 sm:gap-4">
                <div className="stat-icon-glow bg-blue-500/15 text-blue-400 border border-blue-400/30 shadow-[0_0_20px_rgba(59,130,246,0.25)]">
                  <Rocket className="w-5 h-5" />
                </div>
                <div>
                  <div className="text-2xl sm:text-3xl font-extrabold text-white">
                    10<span className="text-blue-400">x</span>
                  </div>
                  <div className="text-xs sm:text-sm text-slate-400">Faster RTL Generation</div>
                </div>
              </div>

              {/* Stat 2 */}
              <div className="flex items-center gap-3.5 sm:gap-4">
                <div className="stat-icon-glow bg-purple-500/15 text-purple-400 border border-purple-400/30 shadow-[0_0_20px_rgba(168,85,247,0.25)]">
                  <Clock className="w-5 h-5" />
                </div>
                <div>
                  <div className="text-2xl sm:text-3xl font-extrabold text-white">
                    70<span className="text-purple-400">%</span>
                  </div>
                  <div className="text-xs sm:text-sm text-slate-400">Less Verification Time</div>
                </div>
              </div>

              {/* Stat 3 */}
              <div className="flex items-center gap-3.5 sm:gap-4">
                <div className="stat-icon-glow bg-cyan-500/15 text-cyan-400 border border-cyan-400/30 shadow-[0_0_20px_rgba(6,182,212,0.25)]">
                  <Lock className="w-5 h-5" />
                </div>
                <div>
                  <div className="text-2xl sm:text-3xl font-extrabold text-white">
                    50<span className="text-cyan-400">+</span>
                  </div>
                  <div className="text-xs sm:text-sm text-slate-400">Supported IP Blocks</div>
                </div>
              </div>

              {/* Stat 4 */}
              <div className="flex items-center gap-3.5 sm:gap-4">
                <div className="stat-icon-glow bg-indigo-500/15 text-indigo-400 border border-indigo-400/30 shadow-[0_0_20px_rgba(99,102,241,0.25)]">
                  <FileCheck className="w-5 h-5" />
                </div>
                <div>
                  <div className="text-2xl sm:text-3xl font-extrabold text-white">
                    100<span className="text-indigo-400">%</span>
                  </div>
                  <div className="text-xs sm:text-sm text-slate-400">Synthesizable Output</div>
                </div>
              </div>
            </div>
          </div>

        </div>
      </section>

      {/* How It Works — 3-step flow, seamlessly blended from hero */}
      <section id="how-it-works" className="py-20 relative overflow-hidden scroll-reveal section-blend-into">
        <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[500px] h-[250px] bg-purple-600/8 blur-[100px] rounded-full pointer-events-none" />
        <div className="absolute top-0 right-1/4 w-[400px] h-[300px] bg-blue-600/6 blur-[120px] rounded-full pointer-events-none" />
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <div className="text-center mb-12">
            <h2 className="text-4xl font-bold text-white mb-4">
              How It <span className="text-transparent bg-clip-text bg-gradient-to-r from-purple-400 to-cyan-400">Works</span>
            </h2>
            <p className="text-lg text-slate-400 max-w-2xl mx-auto">
              From specification to verification in three simple steps
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 relative">
            {/* Step 1 */}
            <div className="text-center relative flow-arrow">
              <div className="w-20 h-20 rounded-2xl flex items-center justify-center mx-auto mb-5 relative bg-blue-500/10 border border-blue-400/20 shadow-[0_0_20px_rgba(59,130,246,0.2)]">
                <FileCode className="w-9 h-9 text-blue-400" />
                <span className="absolute -top-2 -right-2 w-7 h-7 rounded-full bg-blue-600 text-white text-sm font-bold flex items-center justify-center shadow-[0_0_15px_rgba(59,130,246,0.5)]">1</span>
              </div>
              <h3 className="text-lg font-semibold text-white mb-2">Describe Your Design</h3>
              <p className="text-sm text-slate-400 leading-relaxed">
                Specify your chip requirements in natural language — interfaces, timing, protocols.
              </p>
            </div>

            {/* Step 2 */}
            <div className="text-center relative flow-arrow">
              <div className="w-20 h-20 rounded-2xl flex items-center justify-center mx-auto mb-5 relative bg-purple-500/10 border border-purple-400/20 shadow-[0_0_20px_rgba(168,85,247,0.2)]">
                <Cpu className="w-9 h-9 text-purple-400" />
                <span className="absolute -top-2 -right-2 w-7 h-7 rounded-full bg-purple-600 text-white text-sm font-bold flex items-center justify-center shadow-[0_0_15px_rgba(168,85,247,0.5)]">2</span>
              </div>
              <h3 className="text-lg font-semibold text-white mb-2">AI Generates RTL</h3>
              <p className="text-sm text-slate-400 leading-relaxed">
                Our semiconductor-trained AI produces synthesizable Verilog/SystemVerilog instantly.
              </p>
            </div>

            {/* Step 3 */}
            <div className="text-center relative">
              <div className="w-20 h-20 rounded-2xl flex items-center justify-center mx-auto mb-5 relative bg-cyan-500/10 border border-cyan-400/20 shadow-[0_0_20px_rgba(6,182,212,0.2)]">
                <ShieldCheck className="w-9 h-9 text-cyan-400" />
                <span className="absolute -top-2 -right-2 w-7 h-7 rounded-full bg-cyan-600 text-white text-sm font-bold flex items-center justify-center shadow-[0_0_15px_rgba(6,182,212,0.5)]">3</span>
              </div>
              <h3 className="text-lg font-semibold text-white mb-2">Verify & Simulate</h3>
              <p className="text-sm text-slate-400 leading-relaxed">
                Auto-generated testbenches, waveform analysis, and coverage — all integrated.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Demo Section — Product showcase carousel, seamlessly blended */}
      <section id="demo" className="py-20 relative overflow-hidden scroll-reveal section-blend-into">
        <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[600px] h-[300px] bg-blue-600/8 blur-[100px] rounded-full pointer-events-none" />
        <div className="absolute bottom-0 left-1/4 w-[400px] h-[250px] bg-cyan-500/6 blur-[120px] rounded-full pointer-events-none" />
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <div className="text-center mb-12">
            <h2 className="text-4xl font-bold text-white mb-4">
              Experience the <span className="text-transparent bg-clip-text bg-gradient-to-r from-cyan-400 to-blue-400">Platform</span>
            </h2>
            <p className="text-lg text-slate-400 max-w-2xl mx-auto">
              AI copilot integrated directly into your design workflow
            </p>
          </div>

          <div className="showcase-container">
            <div className="showcase-viewport">
              <div className="showcase-track" style={{ transform: `translateX(-${showcaseIndex * 100}%)` }}>
                <div className="showcase-slide">
                  <div className="screenshot-frame">
                    <img src="/cder-chat1.png" alt="ChipDesigner.ai Copilot — Agent mode with tool execution" />
                    <div className="screenshot-label">
                      <Sparkles className="w-3.5 h-3.5" /> AI Copilot — Agent Mode
                    </div>
                  </div>
                </div>
                <div className="showcase-slide">
                  <div className="screenshot-frame">
                    <img src="/cder-elwave.png" alt="ElWave — Integrated waveform viewer for ChipDesigner.ai" />
                    <div className="screenshot-label">
                      <AudioWaveform className="w-3.5 h-3.5" /> ElWave Viewer
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <button
              type="button"
              className="showcase-arrow showcase-arrow-left"
              onClick={() => setShowcaseIndex(prev => (prev - 1 + 2) % 2)}
              aria-label="Previous screenshot"
            >
              <ChevronLeft className="w-4 h-4" />
            </button>
            <button
              type="button"
              className="showcase-arrow showcase-arrow-right"
              onClick={() => setShowcaseIndex(prev => (prev + 1) % 2)}
              aria-label="Next screenshot"
            >
              <ChevronRight className="w-4 h-4" />
            </button>

            <div className="showcase-caption">
              <h3>{showcaseIndex === 0 ? 'AI Copilot — Agent Mode with Tool Execution' : 'ElWave — Integrated Waveform Analysis'}</h3>
              <p>{showcaseIndex === 0 
                ? 'The copilot autonomously reads files, runs simulations, and edits code — showing every step with full transparency.' 
                : 'Visualize simulation results with signal traces, cursors, and value inspection — no external tools needed.'}</p>
            </div>

            <div className="showcase-nav">
              <button 
                type="button"
                className={`showcase-dot ${showcaseIndex === 0 ? 'showcase-active' : ''}`}
                onClick={() => setShowcaseIndex(0)}
                aria-label="Show Copilot Chat"
                style={{ background: showcaseIndex === 0 ? '' : 'rgba(59, 130, 246, 0.2)' }}
              />
              <button 
                type="button"
                className={`showcase-dot ${showcaseIndex === 1 ? 'showcase-active' : ''}`}
                onClick={() => setShowcaseIndex(1)}
                aria-label="Show ElWave Viewer"
                style={{ background: showcaseIndex === 1 ? '' : 'rgba(59, 130, 246, 0.2)' }}
              />
            </div>
          </div>
        </div>
      </section>

      {/* CTA / Contact Section */}
      <section id="contact" className="py-12 relative scroll-reveal section-blend-into">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="rounded-2xl p-10 relative overflow-hidden" style={{ background: 'linear-gradient(135deg, rgba(15, 23, 42, 0.85), rgba(30, 27, 75, 0.85))', border: '1px solid rgba(56, 189, 248, 0.15)', backdropFilter: 'blur(12px)' }}>
            <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[400px] h-[200px] bg-blue-600/10 blur-[80px] rounded-full pointer-events-none" />
            <h2 className="text-3xl font-bold mb-3 text-white text-center relative z-10">
              Get in <span className="text-transparent bg-clip-text bg-gradient-to-r from-cyan-400 to-blue-400">Touch</span>
            </h2>
            <p className="text-base mb-8 text-slate-400 text-center max-w-xl mx-auto relative z-10">
              Have questions about Enterprise plans, custom pricing, or a demo? Send us a message.
            </p>

            {/* Contact Form */}
            <form
              onSubmit={async (e) => {
                e.preventDefault();
                setContactSubmitting(true);
                setContactError('');
                setContactSuccess(false);
                try {
                  const baseUrl = process.env.REACT_APP_API_BASE_URL || '';
                  const resp = await fetch(`${baseUrl}/api/contact/submit`, {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify(contactForm),
                  });
                  if (resp.ok) {
                    setContactSuccess(true);
                    setContactForm({ name: '', email: '', company: '', seats: '', message: '' });
                  } else {
                    const data = await resp.json().catch(() => ({}));
                    setContactError(data.detail || 'Failed to submit. Please try again.');
                  }
                } catch (err) {
                  setContactError('Network error. Please try again.');
                }
                setContactSubmitting(false);
              }}
              className="space-y-4 relative z-10"
            >
              <div className="grid sm:grid-cols-2 gap-4">
                <input
                  type="text"
                  required
                  placeholder="Your Name"
                  value={contactForm.name}
                  onChange={(e) => setContactForm({ ...contactForm, name: e.target.value })}
                  className="w-full px-4 py-3 bg-slate-900/80 border border-slate-700/60 rounded-xl text-white placeholder-slate-500 focus:outline-none focus:border-blue-500 transition-colors"
                />
                <input
                  type="email"
                  required
                  placeholder="Email Address"
                  value={contactForm.email}
                  onChange={(e) => setContactForm({ ...contactForm, email: e.target.value })}
                  className="w-full px-4 py-3 bg-slate-900/80 border border-slate-700/60 rounded-xl text-white placeholder-slate-500 focus:outline-none focus:border-blue-500 transition-colors"
                />
              </div>
              <input
                type="text"
                placeholder="Company (optional)"
                value={contactForm.company}
                onChange={(e) => setContactForm({ ...contactForm, company: e.target.value })}
                className="w-full px-4 py-3 bg-slate-900/80 border border-slate-700/60 rounded-xl text-white placeholder-slate-500 focus:outline-none focus:border-blue-500 transition-colors"
              />
              <input
                type="number"
                min="1"
                placeholder="Number of employees / seats"
                value={contactForm.seats}
                onChange={(e) => setContactForm({ ...contactForm, seats: e.target.value })}
                className="w-full px-4 py-3 bg-slate-900/80 border border-slate-700/60 rounded-xl text-white placeholder-slate-500 focus:outline-none focus:border-blue-500 transition-colors"
              />
              <textarea
                required
                rows="4"
                placeholder="Tell us what you need..."
                value={contactForm.message}
                onChange={(e) => setContactForm({ ...contactForm, message: e.target.value })}
                className="w-full px-4 py-3 bg-slate-900/80 border border-slate-700/60 rounded-xl text-white placeholder-slate-500 focus:outline-none focus:border-blue-500 transition-colors resize-none"
              />

              {contactError && (
                <div className="bg-red-900/40 border border-red-700/50 text-red-300 px-4 py-3 rounded-lg text-sm">
                  {contactError}
                </div>
              )}
              {contactSuccess && (
                <div className="bg-green-900/40 border border-green-700/50 text-green-300 px-4 py-3 rounded-lg text-sm">
                  Thank you! We'll get back to you within 24 hours.
                </div>
              )}

              <button
                type="submit"
                disabled={contactSubmitting}
                className="w-full py-3 bg-gradient-to-r from-blue-600 to-cyan-500 hover:from-blue-500 hover:to-cyan-400 disabled:opacity-50 text-white rounded-xl font-medium transition-all flex items-center justify-center gap-2 shadow-[0_0_25px_rgba(59,130,246,0.4)] cursor-pointer"
                style={{ border: 'none' }}
              >
                {contactSubmitting ? (
                  <><span className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" /> Sending...</>
                ) : (
                  <>Send Message <ArrowRight className="w-4 h-4" /></>
                )}
              </button>
            </form>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="border-t border-cyan-500/10 text-white py-6 relative z-10" style={{ background: 'linear-gradient(to top, rgba(10, 16, 32, 0.8) 0%, rgba(10, 16, 32, 0.3) 70%, transparent 100%)' }}>
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col sm:flex-row items-center justify-between gap-4">
            <div className="flex items-center gap-3">
              {logo && <img src={logo} alt="Elinnos" className="h-6 w-auto" />}
              <span className="text-sm font-bold gradient-text">chipdesigner.ai</span>
            </div>

            <div className="flex items-center gap-6">
              <a href="/#features" className="text-sm text-slate-400 hover:text-white transition-colors">Features</a>
              <a href="/#demo" className="text-sm text-slate-400 hover:text-white transition-colors">Demo</a>
              <a href="https://elinnos.com" target="_blank" rel="noopener noreferrer" className="text-sm text-slate-400 hover:text-white transition-colors">Elinnos</a>
              <a href="mailto:contact@chipdesigner.ai" className="text-sm text-slate-400 hover:text-white transition-colors">Contact</a>
            </div>
          </div>

          <div className="border-t border-cyan-500/10 mt-4 pt-3 text-center">
            <p className="text-xs text-slate-500">&copy; 2026 chipdesigner.ai. All rights reserved.</p>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default LandingPageExact;

